import { commandsCtx } from '@milkdown/kit/core';
import { TextSelection } from '@milkdown/kit/prose/state';
import { $command, $useKeymap } from '@milkdown/kit/utils';

type CardType = 'win' | 'warning' | 'quote' | 'note';

/**
 * Command to insert a new single card at the current cursor position.
 *
 * Usage:
 * ```typescript
 * editor.action(insertCardCommand.key, { cardType: 'win' });
 * ```
 */
export const insertCardCommand = $command('insertCard', () => (cardType: CardType = 'note') => {
  return (state, dispatch) => {
    const { schema, tr, selection } = state;
    const cardNode = schema.nodes.card;

    if (!cardNode) {
      console.error('[insertCardCommand] card node not found in schema');
      return false;
    }

    // Create empty paragraph for card content
    const paragraph = schema.nodes.paragraph.create();

    // Create the card node
    const card = cardNode.create({ cardType }, paragraph);

    // Insert at current position
    if (dispatch) {
      const { $from } = selection;
      tr.insert($from.pos, card);

      // Move cursor into the card
      const newPos = $from.pos + 1;
      tr.setSelection(TextSelection.create(tr.doc, newPos));

      dispatch(tr.scrollIntoView());
    }

    return true;
  };
});

/**
 * Command to convert the current blockquote to a card.
 *
 * Usage:
 * ```typescript
 * editor.action(convertToCardCommand.key, { cardType: 'win' });
 * ```
 */
export const convertToCardCommand = $command(
  'convertToCard',
  () =>
    (cardType: CardType = 'note') => {
      return (state, dispatch) => {
        const { schema, tr, selection } = state;
        const { $from } = selection;
        const cardNode = schema.nodes.card;

        if (!cardNode) return false;

        // Find parent blockquote
        const blockquoteDepth = $from.depth - 1;
        const blockquote = $from.node(blockquoteDepth);

        if (blockquote?.type.name !== 'blockquote') {
          console.warn('[convertToCardCommand] not inside a blockquote');
          return false;
        }

        // Create card with the blockquote's content
        const card = cardNode.create({ cardType }, blockquote.content);

        if (dispatch) {
          const blockquotePos = $from.before(blockquoteDepth);
          tr.replaceWith(blockquotePos, blockquotePos + blockquote.nodeSize, card);
          dispatch(tr.scrollIntoView());
        }

        return true;
      };
    }
);

/**
 * Command to convert a card back to a regular blockquote.
 *
 * Usage:
 * ```typescript
 * editor.action(convertToBlockquoteCommand.key);
 * ```
 */
export const convertToBlockquoteCommand = $command('convertToBlockquote', () => () => {
  return (state, dispatch) => {
    const { schema, tr, selection } = state;
    const { $from } = selection;

    // Find parent card
    const cardDepth = $from.depth - 1;
    const card = $from.node(cardDepth);

    if (card?.type.name !== 'card') {
      console.warn('[convertToBlockquoteCommand] not inside a card');
      return false;
    }

    // Create blockquote with the card's content
    const blockquote = schema.nodes.blockquote.create({}, card.content);

    if (dispatch) {
      const cardPos = $from.before(cardDepth);
      tr.replaceWith(cardPos, cardPos + card.nodeSize, blockquote);
      dispatch(tr.scrollIntoView());
    }

    return true;
  };
});

/**
 * Keyboard shortcuts for card operations.
 *
 * - Mod-Alt-w: Insert Win card
 * - Mod-Alt-a: Insert Warning card (Alert)
 * - Mod-Alt-q: Insert Quote card
 * - Mod-Alt-n: Insert Note card
 */
export const singleCardKeymap = $useKeymap('singleCardKeymap', {
  InsertWinCard: {
    shortcuts: 'Mod-Alt-w',
    command: (ctx) => () => ctx.get(commandsCtx).call(insertCardCommand.key, 'win'),
  },
  InsertWarningCard: {
    shortcuts: 'Mod-Alt-a',
    command: (ctx) => () => ctx.get(commandsCtx).call(insertCardCommand.key, 'warning'),
  },
  InsertQuoteCard: {
    shortcuts: 'Mod-Alt-q',
    command: (ctx) => () => ctx.get(commandsCtx).call(insertCardCommand.key, 'quote'),
  },
  InsertNoteCard: {
    shortcuts: 'Mod-Alt-n',
    command: (ctx) => () => ctx.get(commandsCtx).call(insertCardCommand.key, 'note'),
  },
});
